import React, { useContext } from "react";
import ReactModal from "react-modal";
import styled from "styled-components";

import { FormContext } from "../../contexts/FormContext";
import Button from "../buttons/Button";
import {
  IconPageNextLine,
  IconPagePreviousLine,
  IconRejectLine,
} from "../icons";

const root = document.getElementById("root");
if (root != null) {
  ReactModal.setAppElement(root);
}

const Titlebar = styled.div`
  background-color: #f7f7f7;
  border-bottom: solid 1px #ddd;
  align-items: center;
`;

export const Text = styled.div`
  line-height: 1.5em;

  p:first-child {
    margin-top: 0;
  }
  p:last-child {
    margin-bottom: 0;
  }
`;

const PaginationButtons = styled.div`
  display: flex;
`;

const TitlebarMain = styled.div`
  padding: 1rem;
  display: flex;
`;
const TitlebarProgress = styled.div``;
const TitlebarProgressBar = styled.div`
  height: 3px;
  background-color: #3e8cb3;
  transition: width 0.2s;
`;

const TitlebarToolbar = styled.div`
  margin-right: 1rem;
  align-items: center;
  height: 3rem;
  display: flex;
`;

const CloseButton = styled.div``;
export const PaddedBody = styled.div`
  padding: 1rem;
  box-sizing: border-box;
  height: 100%;
`;

export const BasicFooter = styled.div`
  padding: 1rem;
  border-top: solid 1px #ddd;
  background-color: rgb(247, 247, 247);
  display: flex;
  justify-content: end;
  gap: 0.5rem;
`;

interface ModalBodyProps {
  flexColumn?: boolean;
}

const Body = styled.div<ModalBodyProps>`
  flex: 1;
  height: 100%;
  overflow: auto;

  ${(props) =>
    props.flexColumn &&
    `
    display: flex;
    flex-direction: column;
  `}
`;

const Title = styled.div`
  font-size: 1.7em;
  line-height: 1.7em;
  font-weight: 100;
  font-style: normal;
  margin: 0;
  color: #333;
  flex: 1;
`;

export type ModalProps = Omit<ReactModal.Props, "onRequestClose"> & {
  title?: string;
  /** Define the entire footer */
  footer?: React.ReactNode;
  /** Define just the footer buttons */
  buttons?: React.ReactNode;
  /** Extra tools that appear on right side of the modal titlebar */
  titlebarToolbar?: React.ReactNode;
  /** Render a custom body without `<Body />`. Useful for step modals. */
  customBody?: React.ReactNode;
  styles?: any;
  size?:
    | "xsmall"
    | "small"
    | "medium"
    | "large"
    | "xlarge"
    | "xxlarge"
    | "full"
    | "mobile"
    | "max"; // obscure the entire window
  flexBodyColumn?: boolean;
  fullHeight?: boolean;
  disabled?: boolean;
  showPagination?: boolean;
  onNext?: () => void;
  onPrevious?: () => void;
  onRequestClose?: (event?: React.MouseEvent | React.KeyboardEvent) => void;
  isNextDisabled?: boolean;
  isPreviousDisabled?: boolean;
  progress?: {
    current: number;
    total: number;
  };
};

type ModalSubComponents = {
  Body: typeof Body;
  PaddedBody: typeof PaddedBody;
  BasicFooter: typeof BasicFooter;
};

const Modal: React.FC<React.PropsWithChildren<ModalProps>> &
  ModalSubComponents = ({
  size,
  styles,
  title,
  buttons,
  footer,
  disabled,
  progress,
  fullHeight,
  ...props
}) => {
  let modalStyles: ReactModal.Styles = {
    overlay: {
      alignItems: "start",
    },
    content: {
      flexDirection: "column",
      bottom: "auto",
      left: "auto",
      right: "auto",
      border: size === "mobile" ? "none" : "solid 1px #aaa",
      boxShadow: "0 0 16px rgba(0,0,0,0.25)",
      backgroundColor: "#fff",
      borderRadius: "0.5rem",
      maxWidth: "1200px",
      width: "40rem",
      outline: "none",
      position: "absolute",
      padding: "0",
      height: fullHeight === true ? "calc(100% - 10rem)" : "initial",
    },
  };

  if (styles) {
    modalStyles = { ...modalStyles, ...styles };
  }

  if (size) {
    switch (size) {
      case "mobile":
        modalStyles.content = {
          ...modalStyles.content,
          inset: "0",
          width: "100%",
          maxWidth: "100%",
          borderRadius: "0",
          border: "none",
        };
        break;
      case "max":
        modalStyles.content = {
          ...modalStyles.content,
          inset: "0",
          width: "100%",
          maxWidth: "100%",
          borderRadius: "0",
          boxShadow: "none",
          border: "none",
        };
        break;
      case "full":
        modalStyles.content = {
          ...modalStyles.content,
          right: "4rem",
          left: "4rem",
          bottom: "4rem",
          top: "4rem",
          width: "auto",
          maxWidth: "auto",
        };
        break;
      case "xxlarge":
        modalStyles.content = {
          ...modalStyles.content,
          width: "min(120rem, 100% - 2rem)",
          top: "5rem",
          maxHeight: "calc(100% - 10rem)",
        };
        break;
      case "xlarge":
        modalStyles.content = {
          ...modalStyles.content,
          width: "min(100rem, 100% - 2rem)",
          top: "5rem",
          maxHeight: "calc(100% - 10rem)",
        };
        break;
      case "large":
        modalStyles.content = {
          ...modalStyles.content,
          width: "min(80rem, 100% - 2rem)",
          top: "5rem",
          maxHeight: "calc(100% - 10rem)",
        };
        break;
      case "medium":
        modalStyles.content = {
          ...modalStyles.content,
          width: "55rem",
          top: "5rem",
          maxHeight: "calc(100% - 10rem)",
        };
        break;
      case "small":
        modalStyles.content = {
          ...modalStyles.content,
          width: "40rem",
          top: "5rem",
          maxHeight: "calc(100% - 10rem)",
        };
        break;
      case "xsmall":
        modalStyles.content = {
          ...modalStyles.content,
          width: "28rem",
          top: "5rem",
          maxHeight: "calc(100% - 10rem)",
        };
        break;
    }
  }

  const form = useContext(FormContext);

  return (
    <FormContext.Provider
      value={{
        disabled: (disabled ?? form.disabled) || false,
      }}
    >
      <ReactModal
        closeTimeoutMS={250}
        style={modalStyles}
        shouldCloseOnOverlayClick={false}
        {...props}
      >
        {title && (
          <Titlebar>
            {progress && (
              <TitlebarProgress>
                <TitlebarProgressBar
                  style={{
                    width: `${(progress.current / progress.total) * 100}%`,
                  }}
                />
              </TitlebarProgress>
            )}
            <TitlebarMain>
              <Title>{title}</Title>
              {props.titlebarToolbar && (
                <TitlebarToolbar>{props.titlebarToolbar}</TitlebarToolbar>
              )}
              {props.showPagination && (
                <PaginationButtons>
                  <Button
                    variant="plain"
                    title="Next"
                    onClick={props.onPrevious}
                    disabled={props.isPreviousDisabled ?? false}
                  >
                    <IconPagePreviousLine height="14" />
                  </Button>
                  <Button
                    variant="plain"
                    title="Next"
                    disabled={props.isNextDisabled ?? false}
                    onClick={props.onNext}
                  >
                    <IconPageNextLine height="14" />
                  </Button>
                </PaginationButtons>
              )}
              <CloseButton>
                <Button
                  title="Close Modal"
                  variant="plain"
                  onClick={props.onRequestClose}
                  disabled={false}
                >
                  <IconRejectLine width="16" />
                </Button>
              </CloseButton>
            </TitlebarMain>
          </Titlebar>
        )}
        {!!props.customBody && props.customBody}
        {!props.customBody && (
          <>
            <Body flexColumn={props.flexBodyColumn}>{props.children}</Body>
            {footer ? footer : buttons && <BasicFooter>{buttons}</BasicFooter>}
          </>
        )}
      </ReactModal>
    </FormContext.Provider>
  );
};

Modal.PaddedBody = PaddedBody;
Modal.BasicFooter = BasicFooter;
Modal.Body = Body;

export default Modal;
